import { useParams } from 'react-router';
import { Footer, TitleBar } from '../../../../../components';
import { useLocationName } from '../../../../../hooks';
import { useQualitativeFactorName } from '../../../../../hooks/useQualitativeFactorName';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import { useUserContext } from '../../../../../context/PermissionsContext';
import { QualitativeFactorStats } from './types';
import { useMutation } from '@apollo/client';
import { useQualitativeFactorWorkarounds } from './hooks';
import {
  commonColumns,
  EditableColumnType,
} from '../../../location-applications/shared/workarounds';
import {
  GET_QUALITATIVE_FACTOR_WORKAROUNDS,
  SET_QUALITATIVE_FACTOR_WORKAROUND,
} from './queries';
import { tableSortingMethods } from '../../../../../utils';
import { useWorkaroundForm } from '../../../../../hooks/workarounds/useWorkaroundForm';

const QualitativeFactorWorkarounds = () => {
  const { locationUUID, qualitativeFactorUUID } = useParams();
  const { locationName } = useLocationName(locationUUID);
  const { qualitativeFactorName } = useQualitativeFactorName(
    qualitativeFactorUUID,
  );

  const { canEdit } = useUserContext();

  const columns: EditableColumnType[] = [
    {
      title: 'IT Application',
      dataIndex: 'itApplicationName',
      key: 'itApplicationName',
      editable: false,
      defaultSortOrder: 'ascend',
      width: 150,
      sorter: (a: any, b: any) =>
        tableSortingMethods.string(a.itApplicationName, b.itApplicationName),
      render: (text: string) =>
        text ? (
          <span>{text}</span>
        ) : (
          <span className="text-[#BABABA]">N/A</span>
        ),
    },
    ...commonColumns,
  ];

  const qualitativeFactorIds = qualitativeFactorUUID
    ? [qualitativeFactorUUID]
    : [];

  const {
    data: workarounds,
    loading,
    error,
  } = useQualitativeFactorWorkarounds(qualitativeFactorIds, locationUUID);

  const { formInstance, formFields } = useWorkaroundForm();

  const [setQualitativeFactorWorkaround, { loading: updateLoading }] =
    useMutation(SET_QUALITATIVE_FACTOR_WORKAROUND, {
      refetchQueries: [GET_QUALITATIVE_FACTOR_WORKAROUNDS],
    });

  const modifyVariables = ({ currentRecord, values }: ModifyVariablesArgs) => {
    return {
      qualitativeFactorUuid: qualitativeFactorUUID || '',
      itApplicationUuid: currentRecord?.itApplicationId,
      ...(locationUUID && { locationUuid: locationUUID }),
      isWorkaroundNeeded: Boolean(values.isWorkaroundNeeded),
      workaroundDefined: values.workaroundDefined,
      runtimeHours: values.runtimeHours ? Number(values.runtimeHours) : null,
      maxDataLossHours: values.maxDataLossHours
        ? Number(values.maxDataLossHours)
        : null,
      workaroundDescription: values.workaroundDescription || null,
    };
  };

  const title = `${
    locationUUID ? `Location` : 'Enterprise'
  } Qualitative Factor Workarounds`;
  const subtitle = `${
    locationUUID ? `${locationName}/` : ''
  } ${qualitativeFactorName}`;

  const backPath = `/cyber/${
    locationUUID ? `location-level/${locationUUID}` : `enterprise-level`
  }/qualitative-factors/workarounds`;

  return (
    <div className="p-2">
      <TitleBar
        title={title}
        subtitle={subtitle}
        brief={`Capture and describe how the ${qualitativeFactorName} factor can continue to be supported during a disruption of the following critical applications.`}
      />
      <TableV3
        columns={columns}
        data={workarounds as QualitativeFactorStats[]}
        loading={loading || updateLoading}
        editable
        title="Workaround"
        formFields={formFields}
        updateGQL={setQualitativeFactorWorkaround}
        modifyVariables={modifyVariables}
        editableOptions={{
          update: canEdit,
          delete: false,
        }}
        error={error}
        formInstance={formInstance}
      />
      <Footer backPath={backPath} />
    </div>
  );
};

export default QualitativeFactorWorkarounds;
